import React from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  AccountBalance, 
  TrendingUp, 
  Home, 
  Business,
  AttachMoney,
  DirectionsCar,
  Diamond
} from '@mui/icons-material';
import { useAuthStore } from '../stores/authStore';

const Assets: React.FC = () => {
  const { user } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Hardcoded assets data
  const assets = [
    {
      id: '1',
      name: 'Family Home',
      type: 'property',
      value: 850000,
      description: '4 bedroom house in Sydney CBD'
    },
    {
      id: '2',
      name: 'Investment Property',
      type: 'property',
      value: 650000,
      description: 'Rental property in Melbourne'
    },
    {
      id: '3',
      name: 'Stock Portfolio',
      type: 'investment',
      value: 125000,
      description: 'ASX listed shares and ETFs'
    },
    {
      id: '4',
      name: 'Savings Account',
      type: 'cash',
      value: 45000,
      description: 'Commonwealth Bank savings'
    },
    {
      id: '5',
      name: 'Business Shares',
      type: 'business',
      value: 200000,
      description: '25% stake in consulting firm'
    },
    {
      id: '6',
      name: 'Vehicle',
      type: 'vehicle',
      value: 35000,
      description: '2020 Toyota Camry'
    }
  ];

  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'property': return <Home sx={{ fontSize: 40 }} />;
      case 'investment': return <TrendingUp sx={{ fontSize: 40 }} />;
      case 'business': return <Business sx={{ fontSize: 40 }} />;
      case 'cash': return <AttachMoney sx={{ fontSize: 40 }} />;
      case 'vehicle': return <DirectionsCar sx={{ fontSize: 40 }} />;
      default: return <AccountBalance sx={{ fontSize: 40 }} />;
    }
  };

  const getAssetColor = (type: string) => {
    switch (type) {
      case 'property': return { color: '#4285F4', bgColor: '#E3F2FD' };
      case 'investment': return { color: '#34A853', bgColor: '#E8F5E8' };
      case 'business': return { color: '#FBBC04', bgColor: '#FFF8E1' };
      case 'cash': return { color: '#EA4335', bgColor: '#FFEBEE' };
      case 'vehicle': return { color: '#9C27B0', bgColor: '#F3E5F5' };
      default: return { color: '#607D8B', bgColor: '#ECEFF1' };
    }
  };

  const totalValue = assets.reduce((sum, asset) => sum + asset.value, 0);
  const propertyAssets = assets.filter(a => a.type === 'property');
  const investmentAssets = assets.filter(a => a.type === 'investment');

  const statCards = [
    {
      title: 'Total Assets',
      value: assets.length,
      icon: <Diamond sx={{ fontSize: 40 }} />,
      color: '#4285F4',
      bgColor: '#E3F2FD'
    },
    {
      title: 'Total Value',
      value: `$${(totalValue / 1000).toFixed(0)}K`,
      icon: <AttachMoney sx={{ fontSize: 40 }} />,
      color: '#34A853',
      bgColor: '#E8F5E8'
    },
    {
      title: 'Properties',
      value: propertyAssets.length,
      icon: <Home sx={{ fontSize: 40 }} />,
      color: '#FBBC04',
      bgColor: '#FFF8E1'
    },
    {
      title: 'Investments',
      value: investmentAssets.length,
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#EA4335',
      bgColor: '#FFEBEE'
    }
  ];

  if (!['PRO', 'PLUS'].includes(user?.role || '')) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80,
              bgcolor: '#FF9800',
              mx: 'auto',
              mb: 3
            }}
          >
            <Diamond sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Asset Management
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            This feature is available for PRO and PLUS plans only.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upgrade your plan to access detailed asset management and tracking.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Avatar 
          sx={{ 
            width: { xs: 60, md: 80 }, 
            height: { xs: 60, md: 80 },
            bgcolor: '#4285F4',
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}
        >
          <Diamond sx={{ fontSize: { xs: 30, md: 40 } }} />
        </Avatar>
        <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            sx={{ 
              fontWeight: 600,
              color: '#333',
              mb: 0.5
            }}
          >
            Asset Management
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#666',
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}
          >
            Track and manage your valuable assets
          </Typography>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <Card
              sx={{
                background: `linear-gradient(135deg, ${card.bgColor} 0%, ${card.bgColor}CC 100%)`,
                border: 'none',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
                },
                height: '100%'
              }}
            >
              <CardContent sx={{ 
                p: { xs: 2.5, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <Box
                  sx={{
                    width: { xs: 60, md: 70 },
                    height: { xs: 60, md: 70 },
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: card.color,
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                  }}
                >
                  {card.icon}
                </Box>
                
                <Typography
                  variant={isMobile ? 'h4' : 'h3'}
                  sx={{
                    fontWeight: 700,
                    color: '#333',
                    mb: 1,
                    fontSize: { xs: '1.8rem', md: '2.5rem' }
                  }}
                >
                  {card.value}
                </Typography>
                
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                    fontSize: { xs: '0.85rem', md: '0.9rem' }
                  }}
                >
                  {card.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Assets List */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Asset Details
            </Typography>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Asset</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.map((asset) => {
                  const colors = getAssetColor(asset.type);
                  return (
                    <TableRow key={asset.id} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box 
                            sx={{ 
                              mr: 2,
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              backgroundColor: colors.bgColor,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.color
                            }}
                          >
                            {getAssetIcon(asset.type)}
                          </Box>
                          <Typography sx={{ fontWeight: 500 }}>
                            {asset.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={asset.type.charAt(0).toUpperCase() + asset.type.slice(1)}
                          sx={{
                            backgroundColor: colors.bgColor,
                            color: colors.color,
                            fontWeight: 500
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          ${asset.value.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {asset.description}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Assets;
